import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import merge from 'lodash/merge';
import noop from 'lodash/noop';
import { call, put, select, take } from 'redux-saga/effects';

import * as APSActions from '../../actions';
import * as APSConstants from '../../constants';
import * as D from '../../deps';
import * as APSSelectors from '../../selectors';
import * as APSServices from '../../services';
import * as APSUtils from '../../utils';

const log = D.Logger.child({ component: 'domain/Playback/aps/helpers' });
const spawn = D.taskManagerSpawnFactory(APSConstants.DOMAIN_NAME);

export function* setPlaylist({ item, repeat, shuffle }) {
  const track = APSUtils.mapItemToAudioTrack(item);
  const playlist = APSUtils.buildPlaylistObject({
    audioTrack: track,
    repeat,
    shuffle,
  });

  yield call(D.putAndWait, D.Actions.Playback.setPlaylist(playlist));
  return { playlist, track };
}

export function* peekSource(sourceId) {
  const peeking = yield select(D.Utils.Api.isRequestInFlight, {
    endpoint: D.Constants.Api.ApiEndpoints.PLAYBACK_PEEK,
  });
  if (!peeking) {
    const deviceProperties = yield select(D.getInDomain('analytics.utils.getAPSEventData', {}));
    const deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));

    const data = {
      sourceId,
      deviceProperties,
      deviceUuid,
    };
    const requestData = yield call(hydrateApsRequest, data);
    const result = yield call(APSServices.playbackPeek, requestData);

    if (result.item) {
      yield put(
        D.Actions.AudioPlayer.preloadTrack({
          track: APSUtils.mapItemToAudioTrack(result.item),
        }),
      );
    }
  }
}

export function* logTrackStarted(track, type) {
  const action = yield take([
    D.Constants.AudioPlayer.AudioPlayerActions.PLAYING,
    D.Constants.AudioPlayer.AudioPlayerActions.TRACK_END_ERROR,
  ]);
  const pandoraId = track?.get('pandoraId');
  if (
    action.type === D.Constants.AudioPlayer.AudioPlayerActions.PLAYING &&
    action.payload.token === pandoraId
  ) {
    const sourceId = track?.get('sourceId');
    yield put(
      D.Actions.Integrations.newTrackStartedFromSource({
        id: sourceId,
        track,
        type,
      }),
    );
  }
}

export function* playItem({ item, time, repeat, shuffle, isNew }) {
  yield call(D.putAndWait, APSActions.setCurrentItem({ item }));

  switch (item.type) {
    case APSConstants.APSItemTypes.SimStreamViolation:
      yield put(
        D.PlaybackActions.insertNewSimStreamViolation({
          activeDeviceImageId: item.activeDeviceImageId,
          audioWarningTrack: item.audioUrl,
          dialogueHeader: item.dialogueHeader,
          dialoguePrompt: item.dialoguePrompt,
          errorTimeStamp: Date.now(),
        }),
      );
      yield put(D.PlaybackActions.promptStreamViolation());
      return;
    case APSConstants.APSItemTypes.PremiumAccessEnd:
      yield call(D.putAndWait, D.Actions.Application.listenerReauth(false));
      return;
    case APSConstants.APSItemTypes.VoiceTrack: {
      const enabled = yield select(
        D.getInDomain('playback.utils.areVoiceTracksEnabled'),
        item.sourceId,
      );
      if (!enabled) {
        yield put(
          APSActions.itemEnded({
            sourceId: item.sourceId,
            itemPandoraId: item.pandoraId,
            reason: D.Constants.Playback.APSTrackEndReasons.ERROR,
          }),
        );
        return;
      }
    }
  }

  const { playlist, track } = yield call(setPlaylist, {
    item,
    repeat,
    shuffle,
  });
  const currentTime = isNil(time) ? track.get('currentProgress') : time;
  const isInitialLoad = !(yield select(
    D.getInDomain('playback.utils.hasPlaybackStartedInSession', false),
  ));
  yield put(
    D.Actions.AudioPlayer.playTrack({
      playlistId: track.get('sourceId'),
      track,
      currentTime,
      isInitialLoad,
    }),
  );
  get(
    window,
    'DesktopBridge.playOrPauseMedia',
    noop,
  )(true, D.Utils.SourceType.getDesktopBridgeSourceType(playlist.type));

  if (isNew) {
    yield spawn(logTrackStarted, track, playlist.type);
  }
}

export function* pauseAudioIfPlaying() {
  const isPlaying = yield select(D.PlaybackUtils.isAudioPlaying);
  const nowPlayingTrack = yield select(D.getInDomain('playback.utils.getActivelyPlayingTrack'));
  if (isPlaying && nowPlayingTrack) {
    yield put(D.Actions.AudioPlayer.pauseAudio({ track: nowPlayingTrack }));
  }
}

export function* playbackSource(data) {
  data.deviceProperties = yield select(D.getInDomain('analytics.utils.getAPSEventData', {}));
  data.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));

  const requestData = yield call(hydrateApsRequest, data);

  try {
    const result = yield call(APSServices.playbackSource, requestData);

    result.item.originalSourceId = result.item.sourceId;
    result.item.sourceId = result.source?.pandoraId || data.sourceId;

    return result;
  } catch (error) {
    yield call(onPlaybackSourceFailure, error);
  }
}

export function* playbackResetCurrentSource() {
  const currentItem = yield select(APSSelectors.getCurrentItem);
  const sourceId = currentItem.get('sourceId');
  const sourceType = yield call(D.Utils.SourceType.getSourceTypeFromPandoraId, sourceId);
  return yield call(playbackSource, {
    sourceId:
      sourceType === D.Constants.SourceTypes.SourceTypes.PODCAST
        ? currentItem.get('pandoraId')
        : sourceId,
    shuffle: yield select(APSSelectors.getSourceShuffleMode),
    repeat: yield select(APSSelectors.getSourceRepeatMode),
    index: 0,
  });
}

export function* playbackCurrent(action) {
  const currentItem = yield select(APSSelectors.getCurrentItem);
  action.data.sourceId = currentItem.get('sourceId');
  action.data.deviceProperties = yield select(D.getInDomain('analytics.utils.getAPSEventData', {}));
  action.data.deviceUuid = yield select(D.getInDomain('application.utils.getDeviceUuid', ''));
  const requestData = yield call(hydrateApsRequest, action.data);

  try {
    return yield call(APSServices.playbackCurrent, requestData);
  } catch (error) {
    yield call(onActionEventCurrentFailure, {
      error,
      apsEndpoint: D.Constants.Api.ApiEndpoints.PLAYBACK_CURRENT,
      originalAction: action,
    });
  }
}

export function* setPlaylistAndResetIndex(result) {
  yield call(setPlaylist, {
    item: result.item,
    repeat: get(result, 'source.repeat'),
    shuffle: get(result, 'source.shuffle'),
  });

  yield put(
    D.Actions.Playback.resetPlaylistIndex({
      id: get(result, 'item.sourceId'),
      duration: get(result, 'item.duration'),
    }),
  );
}

export function* onNoPlayableContentError(error) {
  const sourceId = get(error, 'action.content.sourceId');
  const sourceType = D.Utils.SourceType.getSourceTypeFromPandoraId(sourceId);

  switch (sourceType) {
    case D.Constants.SourceTypes.SourceTypes.SONG:
      yield put(
        D.Actions.Modal.openModalCoachmark(D.PlaybackConstants.PlaybackMessages.unavailableSong),
      );
      break;
    case D.Constants.SourceTypes.SourceTypes.ALBUM:
      yield put(
        D.Actions.Modal.openModalCoachmark(D.PlaybackConstants.PlaybackMessages.unavailableAlbum),
      );
      break;
    case D.Constants.SourceTypes.SourceTypes.PLAYLIST:
      // DEVICE-10689: server to return 3 possible errorMessages corresponding
      // to no tracks, radio-only, and unavailable tracks scenarios.
      yield put(
        D.Actions.Modal.openModalCoachmark(
          D.PlaybackConstants.PlaybackMessages.unavailablePlaylist,
        ),
      );
      break;
    case D.Constants.SourceTypes.SourceTypes.PODCAST:
    case D.Constants.SourceTypes.SourceTypes.EPISODE:
      yield put(
        D.Actions.Modal.openModalCoachmark(D.PlaybackConstants.PlaybackMessages.unavailablePodcast),
      );
      break;
  }
}

export function* playFallbackStation() {
  const stationId = yield select(D.getInDomain('playback.utils.getLastPlayedStationId', null));
  if (stationId) {
    const isStationCurrentlyPlaying = yield select(
      D.PlaybackUtils.isSourcePlayingCurrently,
      stationId,
    );
    const isAudioPlaying = yield select(D.PlaybackUtils.isAudioPlaying);
    if (!isStationCurrentlyPlaying || !isAudioPlaying) {
      yield put(
        D.Actions.UseCases.playResumeSource({
          id: stationId,
          pandoraType: D.Constants.SourceTypes.PandoraTypes.USER_STATION,
        }),
      );
    }
  }
}

export function* onNoEntitlementsError(error) {
  const sourceId = get(error, 'action.content.sourceId');
  const index = get(error, 'action.content.index');
  const sortOrder = get(error, 'action.content.sortOrder');
  const context = get(error, 'action.context');

  let targetTrackId;
  if (isNumber(index)) {
    const sourceType = D.Utils.SourceType.getSourceTypeFromPandoraId(sourceId);
    const tracks = yield select(
      D.getInDomain('catalog.utils.getTrackGetterBySourceType')(sourceType),
      sourceId,
      sortOrder,
    );
    targetTrackId = tracks.get(index);
  }

  yield call(
    D.putAndWait,
    D.Actions.UseCases.initiatePremiumAccess({
      targetId: sourceId,
      context,
      targetTrackId,
    }),
  );

  if (yield select(D.Utils.Listener.hasPremiumFeatures)) {
    yield put(
      D.Actions.UseCases.playResumeSource({
        context,
        dontIntercept: true,
        forceRestartSourceIfPlaying: true,
        id: sourceId,
        index,
        shouldShuffle: get(error, 'action.content.shuffle'),
        sortOrder,
      }),
    );
  } else if (!(yield select(D.PlaybackUtils.hasPlaybackStartedInSession))) {
    yield call(playFallbackStation);
  }
}

export function* onPlaybackSourceFailure(error) {
  const errorType = error.errorType;
  const errorMessage = error.errorMessage;
  const sourceId = get(error, 'action.content.sourceId');
  const index = get(error, 'action.content.index');
  const itemId = get(error, 'action.content.itemId');

  switch (errorType) {
    case APSConstants.APSErrorCodes.NO_PLAYABLE_CONTENT:
      yield call(onNoPlayableContentError, error);
      break;
    case APSConstants.APSErrorCodes.STATION_LIMIT_REACHED:
      yield put(
        D.Actions.Modal.openModalCoachmark(
          D.PlaybackConstants.PlaybackMessages.stationLimitReached,
        ),
      );
      break;
    case APSConstants.APSErrorCodes.NO_ENTITLEMENTS:
      yield call(onNoEntitlementsError, error);
      break;
    default:
      yield call(playFallbackStation);
      yield put(
        APSActions.logApsError({
          apsEndpoint: D.Constants.Api.ApiEndpoints.PLAYBACK_SOURCE,
        }),
      );
  }
  log.error(
    `APS playback source error: ${errorType}: ${errorMessage}. sourceId: ${sourceId}, index: ${index}, itemId: ${itemId}`,
  );
}

export function* onSkipLimitReached({ apsEndpoint }) {
  // DEVICE-10159: Always open minicoachmark for now, until rewards-based skipping/replaying is supported on APS
  switch (apsEndpoint) {
    case D.Constants.Api.ApiEndpoints.ACTION_SKIP:
      yield put(
        D.Actions.MiniCoachmark.openMiniCoachmark(
          D.Constants.MiniCoachmark.MiniCoachmarkTypes.SKIP_LIMIT_REACHED,
        ),
      );
      break;
    case D.Constants.Api.ApiEndpoints.ACTION_THUMB_DOWN:
      yield put(
        D.Actions.MiniCoachmark.openMiniCoachmark(
          D.Constants.MiniCoachmark.MiniCoachmarkTypes.THUMB_DOWN,
        ),
      );
      break;
    default:
      yield put(
        D.Actions.MiniCoachmark.openMiniCoachmark(
          D.Constants.MiniCoachmark.MiniCoachmarkTypes.SKIP_LIMIT_REACHED_FLEX,
        ),
      );
  }
}

export function* onActionEventCurrentFailure({ error, apsEndpoint, originalAction }) {
  const errorType = error.errorType;
  const sourceId = get(error, 'action.content.sourceId');

  switch (errorType) {
    case APSConstants.APSErrorCodes.SKIP_LIMIT_REACHED:
    case APSConstants.APSErrorCodes.FLEX_REPLAY_LISTENER_INELIGIBLE:
      yield call(onSkipLimitReached, { apsEndpoint });
      break;
    case APSConstants.APSErrorCodes.SOURCE_ENDED:
    case APSConstants.APSErrorCodes.NO_PLAYABLE_CONTENT:
      yield put(APSActions.resetCurrentSource());
      break;
    case APSConstants.APSErrorCodes.SOURCE_NOT_SET: {
      const source = yield select(APSSelectors.getSource);
      const result = yield call(playbackSource, {
        itemId: yield select(APSSelectors.getCurrentItemItemId),
        repeat: source.get('repeat'),
        shuffle: source.get('shuffle'),
        sortOrder: source.get('sortOrder'),
        sourceId: source.get('pandoraId'),
      });
      if (result) {
        if (source.get('pandoraId') === sourceId) {
          yield put({
            type: originalAction.type,
            data: originalAction.data ?? originalAction.payload, // TODO-PAYLOAD: WEBCA-2505
          });
        } else {
          yield call(playItem, { item: result.item, isNew: true });
        }
      }
      break;
    }
    default:
      yield put(APSActions.logApsError({ apsEndpoint }));
  }
  log.error(
    `APS ${apsEndpoint.split('/').slice(1).join(' ')} error: ${errorType}. sourceId: ${sourceId}.`,
  );
}

export function* hydrateApsRequest(data) {
  const onDemandArtistMessageData = yield select(D.Utils.TunerVars.getOnDemandArtistMessageToken);
  data.onDemandArtistMessageToken = onDemandArtistMessageData;

  /* clear on demand artist data so it is not replayed */
  yield put(D.Actions.TunerVars.clearOnDemandArtistMessageData());

  return data;
}
